import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  useUpdatePropertyCore,
  useUpsertProfile,
  useSetAmenities,
  useCreateUnit,
  useUpdateUnit,
  useDeleteUnit,
  useCreateMedia,
  useSetMediaCategory,
  useReorderMedia,
  useSetMediaCover,
  useDeleteMedia,
} from '@/hooks/api/usePlatform';
import {
  useUpdateMyOverview,
  useUpsertMyGuidelines,
  useSetMyAmenities,
  useCreateMyUnit,
  useUpdateMyUnit,
  useDeleteMyUnit,
  useCreateMyMedia,
  useSetMyMediaCategory,
  useReorderMyMedia,
  useSetMyMediaCover,
  useDeleteMyMedia,
} from '@/hooks/api/useMyProperty';
import type {
  UpdateCoreBody,
  UpsertProfileBody,
  AmenitySelectionInput,
  CreateUnitBody,
  UpdateUnitBody,
  CreateMediaBody,
} from '@/services/api/property-content.api';

/**
 * The property editor's data layer, injected through context so the SAME tab
 * components (`OverviewTab`, `GuidelinesTab`, `RoomsTab` + `RoomFormSlideOver`,
 * `AmenitiesTab`, `PhotosTab`) can serve two surfaces:
 *  - the super-admin Platform Console (`AdminPropertyEditorPage`) → the
 *    `/v1/admin/platform/properties/*` hooks, and
 *  - the hotel Property Listing Editor (`PropertyListingEditorPage`) → the
 *    tenant-scoped `/v1/my-property/*` hooks.
 *
 * Each tab reads its mutation hook from `usePropertyEditorApi()` instead of
 * hard-importing it, and calls it unconditionally at the top of the component.
 * The provider hands down a STABLE bundle object, so every `use*` field is a
 * constant hook reference — satisfying the Rules of Hooks and the linter.
 */

/**
 * The tabs only ever consume `.mutateAsync` and `.isPending`, so the injected
 * mutation surface is deliberately minimal. This lets both providers satisfy one
 * interface even though the two backends return different aggregate shapes
 * (`PropertyContentFull` vs `MyPropertyListing`) — a full `UseMutationResult`
 * generic would drag those result types into the contract needlessly.
 */
export interface EditorMutation<TVariables> {
  mutateAsync: (variables: TVariables) => Promise<unknown>;
  isPending: boolean;
}

export interface PropertyEditorApi {
  useUpdatePropertyCore: (id: string) => EditorMutation<UpdateCoreBody>;
  useUpsertProfile: (id: string) => EditorMutation<UpsertProfileBody>;
  useSetAmenities: (id: string) => EditorMutation<AmenitySelectionInput[]>;
  useCreateUnit: (id: string) => EditorMutation<CreateUnitBody>;
  useUpdateUnit: (id: string) => EditorMutation<{ unitId: string; body: UpdateUnitBody }>;
  useDeleteUnit: (id: string) => EditorMutation<string>;
  useCreateMedia: (id: string) => EditorMutation<CreateMediaBody>;
  useSetMediaCategory: (
    id: string,
  ) => EditorMutation<{ mediaId: string; category: string | null }>;
  useReorderMedia: (id: string) => EditorMutation<string[]>;
  useSetMediaCover: (id: string) => EditorMutation<string>;
  useDeleteMedia: (id: string) => EditorMutation<string>;
  /** F10: read-only role → false. Server still enforces writes independently. */
  canWrite: boolean;
}

const PropertyEditorApiContext = createContext<PropertyEditorApi | null>(null);

export function usePropertyEditorApi(): PropertyEditorApi {
  const value = useContext(PropertyEditorApiContext);
  if (!value) {
    throw new Error('usePropertyEditorApi must be used within a Property editor API provider');
  }
  return value;
}

/**
 * Super-admin bundle — the existing platform hooks, unchanged behaviour. Always
 * writable (the console has no read-only role).
 */
export function PlatformEditorApiProvider({ children }: { children: ReactNode }) {
  const value = useMemo<PropertyEditorApi>(
    () => ({
      useUpdatePropertyCore,
      useUpsertProfile,
      useSetAmenities,
      useCreateUnit,
      useUpdateUnit,
      useDeleteUnit,
      useCreateMedia,
      useSetMediaCategory,
      useReorderMedia,
      useSetMediaCover,
      useDeleteMedia,
      canWrite: true,
    }),
    [],
  );
  return (
    <PropertyEditorApiContext.Provider value={value}>
      {children}
    </PropertyEditorApiContext.Provider>
  );
}

/**
 * Hotel bundle — the tenant-scoped `/v1/my-property/*` hooks mapped onto the
 * same interface field names the tabs expect.
 */
export function MyPropertyEditorApiProvider({
  canWrite,
  children,
}: {
  canWrite: boolean;
  children: ReactNode;
}) {
  const value = useMemo<PropertyEditorApi>(
    () => ({
      useUpdatePropertyCore: useUpdateMyOverview,
      useUpsertProfile: useUpsertMyGuidelines,
      useSetAmenities: useSetMyAmenities,
      useCreateUnit: useCreateMyUnit,
      useUpdateUnit: useUpdateMyUnit,
      useDeleteUnit: useDeleteMyUnit,
      useCreateMedia: useCreateMyMedia,
      useSetMediaCategory: useSetMyMediaCategory,
      useReorderMedia: useReorderMyMedia,
      useSetMediaCover: useSetMyMediaCover,
      useDeleteMedia: useDeleteMyMedia,
      canWrite,
    }),
    [canWrite],
  );
  return (
    <PropertyEditorApiContext.Provider value={value}>
      {children}
    </PropertyEditorApiContext.Provider>
  );
}
